import { useState, useRef, useEffect, useCallback } from 'react';
import './ReelPlayer.css';

const SERVER_BASE = (process.env.REACT_APP_BASE_URL || window.location.origin || '').replace(/\/$/, '');

// Normalize media URLs — relative paths get prefixed with server base
const getMediaUrl = (url) => {
  if (!url) return null;

  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }

  if (url.startsWith('https://')) {
    return url;
  }

  return `${SERVER_BASE}${url.startsWith('/') ? url : `/${url}`}`;
};

export default function ReelPlayerComponent({ reels = [], onAddToCart, onBuyNow }) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [muted, setMuted] = useState(true);
  const [playing, setPlaying] = useState(true);
  const [showProducts, setShowProducts] = useState(true);
  const [progress, setProgress] = useState(0);
  const [dragging, setDragging] = useState(false);

  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const touchStartY = useRef(0);
  const touchStartTime = useRef(0);

  const currentReel = reels[currentIndex];

  // Play/pause when reel changes
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    video.currentTime = 0;
    if (playing) {
      video.play().catch(() => setPlaying(false));
    }
  }, [currentIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handler = (e) => {
      if (e.key === 'ArrowDown' || e.key === 'j') goNext();
      if (e.key === 'ArrowUp' || e.key === 'k') goPrev();
      if (e.key === 'm') setMuted((m) => !m);
      if (e.key === ' ') { e.preventDefault(); togglePlay(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  });

  const goNext = useCallback(() => {
    if (currentIndex < reels.length - 1) setCurrentIndex((i) => i + 1);
  }, [currentIndex, reels.length]);

  const goPrev = useCallback(() => {
    if (currentIndex > 0) setCurrentIndex((i) => i - 1);
  }, [currentIndex]);

  const togglePlay = () => {
    const video = videoRef.current;
    if (!video) return;
    if (video.paused) { video.play(); setPlaying(true); }
    else { video.pause(); setPlaying(false); }
  };

  // Touch swipe handlers
  const onTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
    touchStartTime.current = Date.now();
    setDragging(true);
  };

  const onTouchEnd = (e) => {
    setDragging(false);
    const deltaY = touchStartY.current - e.changedTouches[0].clientY;
    const elapsed = Date.now() - touchStartTime.current;
    if (elapsed < 300 && Math.abs(deltaY) > 50) {
      if (deltaY > 0) goNext();
      else goPrev();
    }
  };

  const onTimeUpdate = () => {
    const video = videoRef.current;
    if (!video || !video.duration) return;
    setProgress((video.currentTime / video.duration) * 100);
  };

  if (!reels.length) {
    return (
      <div className="reel-player-empty">
        <div className="empty-icon">🎬</div>
        <p>No reels to display</p>
        <span>Sync reels from the Reels Manager</span>
      </div>
    );
  }

  const activeProducts = currentReel?.tagged_products?.filter((p) => {
    if (!p.start_time) return true;
    return progress >= (p.start_time / (videoRef.current?.duration || 1)) * 100;
  }) || [];

  return (
    <div
      className="reel-player-container"
      ref={containerRef}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* ── Progress Bar ─────────────────────── */}
      <div className="reel-progress-bar">
        <div className="reel-progress-fill" style={{ width: `${progress}%` }} />
      </div>

      {/* ── Reel Counter ─────────────────────── */}
      <div className="reel-counter">{currentIndex + 1} / {reels.length}</div>

      {/* ── Video ───────────────────────────── */}
      {/* ── CHANGED: src now uses getMediaUrl to support local uploaded/downloaded reels ── */}
      <video
        ref={videoRef}
        className="reel-video"
        src={getMediaUrl(currentReel?.media_url)}
        muted={muted}
        loop
        playsInline
        autoPlay
        onTimeUpdate={onTimeUpdate}
        onClick={togglePlay}
      />

      {/* ── Pause overlay ───────────────────── */}
      {!playing && (
        <div className="play-overlay" onClick={togglePlay}>
          <div className="play-icon">▶</div>
        </div>
      )}

      {/* ── Left / Right nav arrows ──────────── */}
      {currentIndex > 0 && (
        <button className="reel-nav reel-nav-up" onClick={goPrev} aria-label="Previous reel">
          ↑
        </button>
      )}
      {currentIndex < reels.length - 1 && (
        <button className="reel-nav reel-nav-down" onClick={goNext} aria-label="Next reel">
          ↓
        </button>
      )}

      {/* ── Side Controls ────────────────────── */}
      <div className="reel-controls">
        <button
          className={`reel-ctrl-btn ${muted ? 'muted' : ''}`}
          onClick={() => setMuted((m) => !m)}
          title={muted ? 'Unmute' : 'Mute'}
        >
          {muted ? '🔇' : '🔊'}
        </button>
        <button
          className="reel-ctrl-btn"
          onClick={() => setShowProducts((s) => !s)}
          title="Toggle products"
        >
          🛍️
        </button>
        {currentReel?.permalink && (
          <a
            className="reel-ctrl-btn"
            href={currentReel.permalink}
            target="_blank"
            rel="noopener noreferrer"
            title="View on Instagram"
          >
            📱
          </a>
        )}
      </div>

      {/* ── Caption ─────────────────────────── */}
      {currentReel?.caption && (
        <div className="reel-caption">
          <p>{currentReel.caption.slice(0, 120)}{currentReel.caption.length > 120 ? '…' : ''}</p>
        </div>
      )}

      {/* ── Product Overlay ──────────────────── */}
      {showProducts && activeProducts.length > 0 && (
        <div className="reel-products">
          {activeProducts.map((product) => (
            <ProductCard
              key={product.id}
              product={product}
              onAddToCart={() => onAddToCart?.(product)}
              onBuyNow={() => onBuyNow?.(product)}
            />
          ))}
        </div>
      )}

      {/* ── Dot Navigation ───────────────────── */}
      <div className="reel-dots">
        {reels.map((_, i) => (
          <button
            key={i}
            className={`reel-dot ${i === currentIndex ? 'active' : ''}`}
            onClick={() => setCurrentIndex(i)}
          />
        ))}
      </div>
    </div>
  );
}

function ProductCard({ product, onAddToCart, onBuyNow }) {
  const [adding, setAdding] = useState(false);

  const handleAddToCart = async () => {
    setAdding(true);
    await onAddToCart?.();
    setTimeout(() => setAdding(false), 1200);
  };

  return (
    <div className="product-card">
      {product.product_image && (
        <img src={product.product_image} alt={product.product_title} className="product-img" />
      )}
      <div className="product-info">
        <p className="product-name">{product.product_title || 'Product'}</p>
        {product.product_price && (
          <p className="product-price">₹{product.product_price}</p>
        )}
      </div>
      <div className="product-actions">
        {product.cta_type === 'buy_now' ? (
          <button className="product-btn product-btn-primary" onClick={onBuyNow}>
            Buy Now
          </button>
        ) : (
          <button
            className={`product-btn product-btn-primary ${adding ? 'adding' : ''}`}
            onClick={handleAddToCart}
            disabled={adding}
          >
            {adding ? '✓ Added' : 'Add to Cart'}
          </button>
        )}
      </div>
    </div>
  );
}
